// src/receptionist/flow.ts
//
// Booking Flow — the merchant-side adaptation of the Customizable Hiring Flow
// (@type/hiring-flow, builder containers/hiring-flow-builder.tsx). The shapes
// are deliberately kept identical where the meaning is identical, so the two
// products can be folded back onto one canvas component later:
//
//   hiring                     receptionist
//   ------                     ------------
//   ordered steps              ordered steps                     (same)
//   advance: auto | review     advance: auto | review            (same)
//   answer_knockout gate       answer_knockout gate              (same)
//   deadline gate              deadline gate                     (same)
//   channel: whatsapp_first    channel: whatsapp                 (WhatsApp only)
//   chat_native                (always true — there is no link-out product)
//
// What changes is the STEP CATALOGUE. Hiring screens a candidate; we take a
// booking. See the receptionist PRD §3.2 (capture, then staff confirm), which
// is expressed here as the final collect step carrying advance: "review".

export type BookingStepType =
    // Ask for the treatment the customer wants, constrained to the services
    // marked bookable in Services & FAQs.
    | "collect_service"
    // Ask for a preferred day + time. Free text; the extractor normalises it.
    | "collect_time"
    // Name, and a callback number when it differs from the WhatsApp number.
    | "collect_details"
    // A set of merchant-written questions. The generic workhorse, and the
    // direct analogue of the hiring "form" step.
    | "questions"
    // Questions that can gate: first visit, pregnancy, active skin condition,
    // age. Carries answer_knockout so a clinic can flag rather than book.
    | "screening"
    // Ask the customer to send photos or a referral letter in the thread.
    // The WhatsApp media matcher behind this is already built for hiring.
    | "documents"
    // A fixed merchant-written message. No gate, always auto-advances.
    | "message"
    // Send a link out of the thread: a deposit payment link, a map, a form.
    | "external_link";

export type BookingAdvance = "auto" | "review";
export type BookingChannel = "whatsapp";
export type KnockoutOperator = "lt" | "lte" | "eq" | "neq" | "gt" | "gte";

/** Flag and pause. Never auto-declines a customer — same guardrail as hiring. */
export interface AnswerKnockoutGate {
    type: "answer_knockout";
    question_ref: string;
    operator: KnockoutOperator;
    value: string;
    on_fail?: { action: "flag" };
    /** Escalate to the staff channel rather than merely pausing (PRD §3.6). */
    escalate?: boolean;
}

export interface DeadlineGate {
    type: "deadline";
    /** "same_day_17:00" or the relative "+2h" — the hiring grammar, unchanged. */
    due: string;
    /** Offsets BEFORE the deadline, e.g. ["-1h"]. Sent as approved templates. */
    reminders?: string[];
    on_miss?: { action: "hold" };
}

export type BookingGate = AnswerKnockoutGate | DeadlineGate;

export interface BookingQuestion {
    ref: string;
    prompt: string;
    /** Free text unless options are given, in which case it is a choice. */
    options?: string[];
}

export interface BookingFlowStep {
    id: string;
    type: BookingStepType;
    /** Default "auto". The capture step ends "review" so staff confirm (§3.2). */
    advance?: BookingAdvance;
    /** OR semantics across knockouts; at most one deadline per step. */
    gates?: BookingGate[];
    /** questions + screening */
    questions?: BookingQuestion[];
    /** documents: the checklist the media matcher ticks off. */
    items?: string[];
    /** message */
    message?: string;
    /** external_link */
    label?: string;
    url?: string;
}

/**
 * What the AI says on FIRST contact, before any step runs. Separate from the
 * steps because it fires on every inbound conversation, booking-intent or not.
 */
export interface Opener {
    newCustomerOpen: string;
    newCustomerClosed: string;
    returningCustomer: string;
    /** Offer the bookable service list unprompted in the opener. */
    offerServiceMenu: boolean;
}

/**
 * Follow-ups are NOT steps, and the difference is a platform rule rather than a
 * modelling preference. A step is a turn inside a live conversation, so it is
 * always within WhatsApp's 24-hour customer service window and can be free
 * text. A follow-up fires on a schedule, usually hours or days later, which
 * puts it OUTSIDE the window: Meta then accepts only a pre-approved template.
 *
 * Modelling them as steps would let a merchant compose something that is
 * rejected at send time, so they are their own surface with their own
 * template-status guardrail (see validateFlow).
 */
export type FollowUpTrigger =
    | "booking_confirmed"
    | "before_appointment"
    | "after_appointment"
    | "flow_stalled"
    | "no_show";

export const FOLLOW_UP_TRIGGER_LABELS: Record<FollowUpTrigger, string> = {
    booking_confirmed: "When your team confirms a booking",
    before_appointment: "Before the appointment",
    after_appointment: "After the appointment",
    flow_stalled: "When someone goes quiet mid-booking",
    no_show: "When someone does not turn up",
};

export type StopRule = "none" | "customer_replied" | "rebooked";

export const STOP_RULE_LABELS: Record<StopRule, string> = {
    none: "Always send",
    customer_replied: "Skip if they have already replied",
    rebooked: "Skip if they have already rebooked",
};

/** Meta approval state. Anything outside the 24h window needs "approved". */
export type TemplateStatus = "approved" | "pending" | "not_submitted";

export interface FollowUp {
    id: string;
    enabled: boolean;
    label: string;
    trigger: FollowUpTrigger;
    /** Hours from the trigger. Negative is before it. */
    offsetHours: number;
    message: string;
    templateStatus: TemplateStatus;
    stopIf: StopRule;
}

export interface BookingFlow {
    version: number;
    /** Provenance only; "ourteam_default" when untouched. */
    template: string;
    channel: BookingChannel;
    /** First contact, before any step. */
    opener: Opener;
    /** The capture conversation. */
    steps: BookingFlowStep[];
    /** Scheduled messages around the appointment. */
    followUps: FollowUp[];
}

/**
 * Whether a follow-up lands outside WhatsApp's 24-hour window and therefore
 * requires an approved template. A stall nudge inside the first day is the only
 * case that can be free text.
 */
export function needsTemplate(f: FollowUp): boolean {
    if (f.trigger === "flow_stalled") return Math.abs(f.offsetHours) >= 24;
    return true;
}

export function describeOffset(f: FollowUp): string {
    const h = Math.abs(f.offsetHours);
    const unit = h >= 24 && h % 24 === 0 ? `${h / 24} day${h / 24 === 1 ? "" : "s"}` : `${h} hour${h === 1 ? "" : "s"}`;
    if (f.trigger === "booking_confirmed") return h === 0 ? "Straight away" : `${unit} later`;
    if (f.trigger === "before_appointment") return `${unit} before`;
    if (f.trigger === "after_appointment") return `${unit} after`;
    if (f.trigger === "flow_stalled") return `After ${unit} of silence`;
    return `${unit} after`;
}

// Guardrail carried over from the hiring epic: a customer will not sit through
// a long interrogation on WhatsApp, and neither will a candidate.
export const MAX_FLOW_STEPS = 8;

export const STEP_TYPE_LABELS: Record<BookingStepType, string> = {
    collect_service: "Which treatment",
    collect_time: "Preferred time",
    collect_details: "Customer details",
    questions: "Ask questions",
    screening: "Screening questions",
    documents: "Ask for photos or documents",
    message: "Send a message",
    external_link: "Send a link",
};

export const STEP_TYPE_DESCRIPTIONS: Record<BookingStepType, string> = {
    collect_service: "The AI asks which service, using only the ones you marked bookable.",
    collect_time: "The AI asks for a preferred day and time. It never promises the slot.",
    collect_details: "Name, and a callback number if it differs from the WhatsApp number.",
    questions: "Your own questions, asked one at a time.",
    screening: "Questions that can flag a booking for your team instead of taking it.",
    documents: "Ask the customer to send photos or a document in the chat.",
    message: "A fixed message from you. Nothing is collected.",
    external_link: "Send a deposit link, a map or a form. The one step that leaves the chat.",
};

export const KNOCKOUT_OPERATOR_LABELS: Record<KnockoutOperator, string> = {
    eq: "is",
    neq: "is not",
    lt: "is less than",
    lte: "is at most",
    gt: "is more than",
    gte: "is at least",
};

/** Preset checklists for the documents step, mirroring hiring's DOCUMENT_TEMPLATE. */
export const DOCUMENT_PRESETS: { key: string; label: string }[] = [
    { key: "concern_photo", label: "Photo of the area of concern" },
    { key: "referral_letter", label: "Referral letter" },
    { key: "previous_treatment", label: "Record of previous treatment" },
    { key: "id_document", label: "IC or passport" },
    { key: "consent_form", label: "Signed consent form" },
];

/** Reminder offsets offered for a deadline gate. */
export const REMINDER_PRESETS: { value: string; label: string }[] = [
    { value: "-24h", label: "24 hours before" },
    { value: "-4h", label: "4 hours before" },
    { value: "-1h", label: "1 hour before" },
];

export function stepGates(step: BookingFlowStep): BookingGate[] {
    return step.gates ?? [];
}

export function newStepId(): string {
    return `s_${Math.random().toString(36).slice(2, 9)}`;
}

export function newFollowUpId(): string {
    return `f_${Math.random().toString(36).slice(2, 9)}`;
}

export function defaultOpener(businessName = "us"): Opener {
    return {
        newCustomerOpen: `Hello! Welcome to ${businessName}. I can help with treatments, prices and bookings. How may I help you today?`,
        newCustomerClosed: `Hello! We are closed at the moment. I can still answer questions and take a booking request, and the team will confirm when we open.`,
        returningCustomer: `Welcome back! Would you like to book the same treatment as last time?`,
        offerServiceMenu: false,
    };
}

/**
 * The follow-ups every merchant starts with. The first three are the PRD's
 * reminders and drop-off nudge; the last two are the revenue recovery the
 * market scan found merchants paying for elsewhere.
 */
export function defaultFollowUps(): FollowUp[] {
    return [
        {
            id: newFollowUpId(), enabled: true, label: "Booking confirmed",
            trigger: "booking_confirmed", offsetHours: 0,
            message: "Your {service} on {date} at {time} is confirmed. See you then!",
            templateStatus: "approved", stopIf: "none",
        },
        {
            id: newFollowUpId(), enabled: true, label: "Day-before reminder",
            trigger: "before_appointment", offsetHours: -24,
            message: "Reminder: your {service} is tomorrow at {time}. Reply 1 to confirm, 2 to reschedule.",
            templateStatus: "approved", stopIf: "none",
        },
        {
            id: newFollowUpId(), enabled: true, label: "Same-day reminder",
            trigger: "before_appointment", offsetHours: -3,
            message: "See you soon! Your appointment is at {time} today. {directions}",
            templateStatus: "approved", stopIf: "none",
        },
        {
            id: newFollowUpId(), enabled: true, label: "Nudge after silence",
            trigger: "flow_stalled", offsetHours: 3,
            message: "Still keen on {service}? Reply with a day and time and I will pass it to the team.",
            templateStatus: "approved", stopIf: "customer_replied",
        },
        {
            id: newFollowUpId(), enabled: false, label: "Post-visit rebooking",
            trigger: "after_appointment", offsetHours: 72,
            message: "Hope you are happy with your {service}. Most clients come back in 4 weeks. Shall I put a slot request in?",
            templateStatus: "not_submitted", stopIf: "rebooked",
        },
        {
            id: newFollowUpId(), enabled: false, label: "Missed appointment",
            trigger: "no_show", offsetHours: 2,
            message: "Sorry we missed you today. Would you like to rebook?",
            templateStatus: "not_submitted", stopIf: "rebooked",
        },
    ];
}

/**
 * The out-of-the-box flow every merchant starts on: ask what, ask when, ask
 * who, then hold for a human. Matches the Booking capture flow in the PRD and
 * the prototype's scripted booking exactly.
 */
export function defaultBookingFlow(): BookingFlow {
    return {
        version: 1,
        template: "ourteam_default",
        channel: "whatsapp",
        opener: defaultOpener("Ownlab Secret Skin"),
        followUps: defaultFollowUps(),
        steps: [
            { id: newStepId(), type: "collect_service", advance: "auto" },
            { id: newStepId(), type: "collect_time", advance: "auto" },
            { id: newStepId(), type: "collect_details", advance: "review" },
        ],
    };
}

/** Clinic starter: adds a contraindication screen that flags rather than books. */
export function clinicBookingFlow(): BookingFlow {
    const flow = defaultBookingFlow();
    flow.template = "ourteam_clinic";
    flow.steps.splice(2, 0, {
        id: newStepId(),
        type: "screening",
        advance: "auto",
        questions: [
            { ref: "first_visit", prompt: "Is this your first visit with us?", options: ["Yes", "No"] },
            { ref: "skin_condition", prompt: "Do you have any active skin condition or allergy we should know about?", options: ["Yes", "No"] },
        ],
        gates: [
            {
                type: "answer_knockout",
                question_ref: "skin_condition",
                operator: "eq",
                value: "Yes",
                on_fail: { action: "flag" },
                escalate: true,
            },
        ],
    });
    return flow;
}

/**
 * Save-time validation, mirroring the hiring builder's rules. Returns a list of
 * human-readable problems; an empty list means the flow is publishable.
 */
export function validateFlow(flow: BookingFlow): string[] {
    const errors: string[] = [];
    if (flow.steps.length === 0) errors.push("A flow needs at least one step.");
    if (flow.steps.length > MAX_FLOW_STEPS)
        errors.push(`A flow can have at most ${MAX_FLOW_STEPS} steps.`);

    const last = flow.steps[flow.steps.length - 1];
    if (last && last.advance !== "review")
        errors.push("The last step must end with your team confirming, so nothing is promised automatically.");

    flow.steps.forEach((step, i) => {
        const where = `Step ${i + 1} (${STEP_TYPE_LABELS[step.type]})`;
        if (step.type === "questions" || step.type === "screening") {
            const asked = (step.questions || []).filter((q) => (q.prompt || "").trim());
            // A blank row counts for nothing. A new step arrives with one empty
            // question, so a length check alone passes a step that asks nothing
            // and the AI would skip straight past it.
            if (!asked.length) errors.push(`${where} needs at least one question.`);
            else if (asked.length < (step.questions || []).length)
                errors.push(`${where} has an empty question. Write it or remove the row.`);
        }
        if (step.type === "documents" && !(step.items || []).length)
            errors.push(`${where} needs at least one item on the checklist.`);
        if (step.type === "message" && !(step.message || "").trim())
            errors.push(`${where} needs a message.`);
        if (step.type === "external_link" && !(step.url || "").trim())
            errors.push(`${where} needs a link.`);

        const gates = stepGates(step);
        if (gates.filter((g) => g.type === "deadline").length > 1)
            errors.push(`${where} can have only one deadline.`);
        gates.forEach((g) => {
            if (g.type === "answer_knockout") {
                const known = (step.questions || []).some((q) => q.ref === g.question_ref);
                if (!known) errors.push(`${where} has a condition on a question that is no longer there.`);
            }
        });
    });

    flow.followUps.filter((f) => f.enabled).forEach((f) => {
        if (!f.message.trim()) errors.push(`"${f.label}" has no message.`);
    });

    if (!flow.opener.newCustomerOpen.trim()) errors.push("The opener for a new customer is empty.");

    return errors;
}

/**
 * Conditions that make a follow-up fail AT SEND TIME, not at save time.
 *
 * Outside the 24-hour window Meta accepts only an approved template, so an
 * enabled follow-up without one will not deliver. That is worth saying on the
 * screen, but it is NOT a reason to refuse the save: the template is fixed on
 * the templates surface, and the flow the merchant is editing may not even
 * touch follow-ups. Treating it as an error locked every new merchant out of
 * the whole Conversation screen — the shipped default flow enables four
 * follow-ups and submits no templates, so the opener could never be edited.
 *
 * The wording is kept character-identical to the server's own message so the
 * two dedupe into one line if the server also reports it.
 */
export function flowWarnings(flow: BookingFlow): string[] {
    return flow.followUps
        .filter((f) => f.enabled && needsTemplate(f) && f.templateStatus !== "approved")
        .map((f) => `"${f.label}" sends outside the 24-hour window, so it needs an approved WhatsApp template. Currently ${f.templateStatus === "pending" ? "awaiting Meta approval" : "not submitted"}.`);
}

/** A template's real state, as the WhatsApp endpoint reports it: the flow's own copy plus "rejected". */
export type LiveTemplateStatus = TemplateStatus | "rejected";

/**
 * Which template a follow-up sends with: the backend's own rule
 * (booking_flow_doc.template_key_for_follow_up), so the console shows the
 * status of the template that actually goes out.
 */
export function templateKeyFor(f: Pick<FollowUp, "trigger" | "offsetHours">): string | null {
    switch (f.trigger) {
        case "booking_confirmed": return "booking_confirmed";
        case "before_appointment": return Math.abs(f.offsetHours) >= 12 ? "reminder_24h" : "reminder_same_day";
        case "flow_stalled": return "stall_nudge";
        case "after_appointment": return "post_visit_rebook";
        case "no_show": return "no_show_recovery";
        default: return null;
    }
}
